import * as R from 'ramda'
import events from '@/constants/events'
import EventBus from '@/EventBus'
import random from '@/utils/random'

const EVENT = {
  TAB_FOCUSSED: 'TAB_FOCUSSED',
  NEW_INTERVAL: 'NEW_INTERVAL'
}

/**
 * This lets running a callback every few milliseconds with some advanced features.
 * Interval for browser tab active/inactive state can be different
 * A threshold can also be specified, on basis of which callback is executed immediately if user is coming back to the tab after being away for specified time
 *
 * API Doc:
 *
 * const cronObj = cron.start(callback, {
 *   interval: 10000, // interval when tab is active
 *   tabBlurInterval:20000, // interval when tab in in background
 *   blurRefetchTimeout: 10000 // if user comes back to app after being away for 10 seconds, callback is executed again
 * })
 *
 * To stop the cron:
 * cronObj.stop()
 */
const cron = () => {
  let subscribers = []
  let blurTime = -Infinity
  let isTabFocus = !document.hidden
  const startCron = ({ callback, options, cronId }) => {
    let interval = options.interval
    if (!isTabFocus && options.tabBlurInterval) {
      interval = options.tabBlurInterval
    }
    if (cronId) {
      stopCron(cronId)
    }
    return setInterval(callback.bind(null, EVENT.NEW_INTERVAL), interval)
  }

  const stopCron = cronId => clearInterval(cronId)

  const setCronId = (id, cronId) => {
    const index = subscribers.findIndex(s => s.id === id)
    subscribers = R.update(
      index,
      {
        ...subscribers[index],
        cronId
      },
      subscribers
    )
  }

  EventBus.$on(events.TAB_FOCUS, () => {
    isTabFocus = true
    const totalInactiveTime = Date.now() - blurTime
    subscribers.forEach(subscriber => {
      let refetchTimeout = subscriber.options.blurRefetchTimeout
      if (refetchTimeout && refetchTimeout < totalInactiveTime) {
        subscriber.callback(EVENT.TAB_FOCUSSED)
      }
      if (subscriber.options.tabBlurInterval) {
        // restart interval with interval defined for focus state
        let cronId = startCron(subscriber)
        setCronId(subscriber.id, cronId)
      }
    })
  })

  EventBus.$on(events.TAB_BLUR, () => {
    isTabFocus = false
    blurTime = new Date()
    subscribers.forEach(subscriber => {
      if (subscriber.options.tabBlurInterval) {
        const cronId = startCron(subscriber)
        setCronId(subscriber.id, cronId)
      }
    })
  })

  return {
    start(callback, options) {
      const id = random(5)
      const subscriber = {
        id,
        callback,
        options
      }
      const cronId = startCron(subscriber)
      subscriber.cronId = cronId
      subscribers.push({
        id,
        cronId,
        callback,
        options
      })
      return {
        stop() {
          let subscriber = subscribers.find(subscriber => subscriber.id === id)
          subscribers = subscribers.filter(subscriber => subscriber.id !== id)
          stopCron(subscriber.cronId)
        }
      }
    },
    event: EVENT
  }
}

export default cron()
